import { motion, useReducedMotion, AnimatePresence } from 'framer-motion'
import { useEffect, useState } from 'react'

type Props = { badge: string; title: string; next: string }

const EASE = [0.22, 1, 0.36, 1] as const
const CYCLE_MS = 3600 // one box ships out per cycle
const WINDOW = 4 // number of visible boxes

// Ascending expiry dates, 3 months apart from a FIXED base (prerender-safe: no
// `new Date()`, so SSG output and client hydration agree). `pos` grows without
// bound so the board keeps advancing realistically as boxes ship out.
const BASE_MONTH = 2026 * 12 + 2 // March 2026 (0-based month index)
function dateFor(pos: number): string {
  const total = BASE_MONTH + pos * 3
  const m = (total % 12) + 1
  const y = Math.floor(total / 12) % 100
  return `${String(m).padStart(2, '0')}/${String(y).padStart(2, '0')}`
}

export default function AfscaFefo({ badge, title, next }: Props) {
  const reduce = useReducedMotion()

  // `cursor` = absolute index of the current nearest-expiry (head) box. Each cycle
  // the head box (key=cursor) leaves the visible window → AnimatePresence fires its
  // exit (slides out right); a new box (key=cursor+WINDOW) enters at the right; the
  // middle boxes shift left via layout. THIS is the FEFO depletion.
  const [cursor, setCursor] = useState(0)

  useEffect(() => {
    if (reduce) return
    const id = setInterval(() => setCursor((c) => c + 1), CYCLE_MS)
    return () => clearInterval(id)
  }, [reduce])

  // Visible absolute positions [cursor … cursor+WINDOW-1] — UNIQUE keys so
  // AnimatePresence actually removes the head and runs its exit animation.
  const positions = Array.from({ length: WINDOW }, (_, k) => cursor + k)

  return (
    <div className="relative aspect-[4/3] sm:aspect-[16/10] w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      {/* breathing halos */}
      <motion.div
        aria-hidden
        animate={reduce ? undefined : { scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -right-24 -top-20 w-72 h-72 rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.18), transparent 70%)' }}
      />
      <motion.div
        aria-hidden
        animate={reduce ? undefined : { scale: [1, 1.08, 1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute -left-24 -bottom-24 w-80 h-80 rounded-full blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
      />

      {/* Top header — badge / title kicker */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 flex items-start justify-between gap-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/90 backdrop-blur-sm px-2.5 py-1 text-[10px] sm:text-xs text-accent-800">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{badge}</span>
        </span>
        <span className="hidden sm:inline-block text-right text-[10px] sm:text-xs leading-tight text-ink-500 max-w-[55%]">
          <span className="font-mono tracking-[0.18em] text-ink-400">DLC</span>{' '}
          <span className="text-ink-600">{title}</span>
        </span>
      </div>

      {/* ───────────── FEFO conveyor row ───────────── */}
      <div className="absolute inset-x-3 sm:inset-x-8 top-1/2 -translate-y-[54%]">
        {/* conveyor baseline + flow arrow toward exit (right) */}
        <div aria-hidden className="absolute left-0 right-0 top-[78%] h-px">
          <div
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(59,93,255,0.10) 0%, rgba(59,93,255,0.45) 65%, rgba(16,185,129,0.55) 100%)',
            }}
          />
          {!reduce && (
            <motion.span
              animate={{ x: ['-2%', '102%'], opacity: [0, 1, 1, 0] }}
              transition={{ duration: CYCLE_MS / 1000, ease: 'easeInOut', repeat: Infinity }}
              className="absolute top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-accent-500"
              style={{ boxShadow: '0 0 14px 3px rgba(59,93,255,0.55)' }}
            />
          )}
        </div>

        {/* exit marker on the right */}
        <div className="absolute -right-1 sm:right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 text-emerald-600">
          <span className="hidden sm:inline text-[9px] font-semibold uppercase tracking-[0.18em]">
            OUT
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 12h13M13 6l6 6-6 6" />
          </svg>
        </div>

        {/* boxes */}
        <div className="relative flex items-end justify-start gap-2 sm:gap-3 pr-10 sm:pr-12">
          <AnimatePresence initial={false} mode="popLayout">
            {positions.map((p, idx) => {
              const isNearest = idx === 0
              return (
                <motion.div
                  key={p}
                  layout={!reduce}
                  initial={reduce ? false : { opacity: 0, scale: 0.85, x: -8 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={
                    reduce
                      ? { opacity: 0 }
                      : { opacity: 0, x: 120, scale: 0.9, transition: { duration: 0.7, ease: EASE } }
                  }
                  transition={{ duration: 0.6, ease: EASE }}
                  className="relative flex-1 min-w-0"
                >
                  <ProductBox date={dateFor(p)} nearest={isNearest} nextLabel={next} reduce={!!reduce} />
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* kicker — FEFO acronym, subtle */}
      <div className="absolute inset-x-0 bottom-4 sm:bottom-5 flex justify-center">
        <span className="font-mono text-[9px] sm:text-[10px] tracking-[0.28em] uppercase text-ink-400">
          First · Expired · First · Out
        </span>
      </div>
    </div>
  )
}

// ───────────── Single product box ─────────────

function ProductBox({
  date,
  nearest,
  nextLabel,
  reduce,
}: {
  date: string
  nearest: boolean
  nextLabel: string
  reduce: boolean
}) {
  return (
    <div className="relative">
      {/* "Part en premier" tag above the nearest box */}
      <AnimatePresence>
        {nearest && (
          <motion.span
            initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 6, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.9 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="absolute -top-6 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap rounded-full border border-emerald-300 bg-emerald-500 px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold leading-none text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,0.8)]"
          >
            {nextLabel}
          </motion.span>
        )}
      </AnimatePresence>

      {/* the box card */}
      <motion.div
        animate={{
          borderColor: nearest ? 'rgba(16,185,129,0.55)' : 'rgba(14,14,20,0.08)',
          boxShadow: nearest
            ? '0 26px 50px -22px rgba(16,185,129,0.55), 0 0 0 1px rgba(16,185,129,0.25)'
            : '0 12px 28px -18px rgba(14,14,20,0.16), 0 0 0 1px rgba(14,14,20,0.03)',
          backgroundColor: nearest ? 'rgba(236,253,245,0.95)' : 'rgba(255,255,255,0.82)',
          y: nearest && !reduce ? -3 : 0,
        }}
        transition={{ duration: 0.6, ease: EASE }}
        className="relative overflow-hidden rounded-xl sm:rounded-2xl border backdrop-blur-sm px-2 py-2.5 sm:px-3 sm:py-3.5"
      >
        {/* pulsing ring on nearest */}
        {nearest && !reduce && (
          <motion.span
            aria-hidden
            animate={{ scale: [1, 1.35], opacity: [0.4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
            className="absolute inset-0 rounded-xl sm:rounded-2xl border border-emerald-400 pointer-events-none"
          />
        )}

        {/* box glyph */}
        <div className="flex items-center justify-center">
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke={nearest ? '#10b981' : '#8c8c97'}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="sm:w-7 sm:h-7"
          >
            <path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
            <path d="M3 8l9 5 9-5M12 13v8" />
            <path d="M7.5 5.5l9 5" />
          </svg>
        </div>

        {/* DLC date label */}
        <div className="mt-1.5 flex flex-col items-center gap-0.5">
          <span
            className={`font-mono text-[10px] sm:text-xs font-semibold tabular-nums tracking-tight ${
              nearest ? 'text-emerald-700' : 'text-ink-600'
            }`}
          >
            {date}
          </span>
          <span
            className={`text-[7px] sm:text-[8px] font-medium uppercase tracking-[0.18em] ${
              nearest ? 'text-emerald-500' : 'text-ink-400'
            }`}
          >
            DLC
          </span>
        </div>
      </motion.div>
    </div>
  )
}
